import {
  format,
  isSameMinute,
  isAfter,
  isBefore,
  parseISO,
  startOfMinute,
  isSameDay,
} from "date-fns";
import { fgInvert } from "@/services/colors";
import {
  type MappedQGLTask,
  type PlannerTask,
} from "@/graphql/services/planner/getPlannerTasks";
import {
  type IActorIds,
  type IEditEntry,
  type ISubTask,
  type ITaskNotificationSettings,
} from "@/planner-interfaces";
import {
  Approval_State,
  Container_Type,
  type ImageUpload,
  Planner_Notification_State,
  type PlannerNotificationSettingsInput,
  type PlannerSubtaskInput,
  Plannertask_Assignee_Type,
  type PlannerTaskAssigneeInput,
  type PlannerTaskCreateInput,
  type PlannerTaskEditInput,
  type ProfileInput,
} from "@/graphql/generated-types";
import type { ExtendedItem } from "@/interfaces";
import type { HourFormatTypes } from "@/types";
import toRRuleInput from "@/helpers/rrules/toRRuleInput";

/**
 * Modify/decorate some properties on an entry
 * to convert database values to useful Javascript values.
 */
export function normalizeTask(
  entry: MappedQGLTask,
  overrideKey = null,
): PlannerTask {
  const start =
    typeof entry.start === "string" ? parseISO(entry.start) : entry.start;
  const end = typeof entry.end === "string" ? parseISO(entry.end) : entry.end;
  const color = entry.color || "#ffffff";

  return {
    ...entry,
    start,
    end,
    color,
    inverted: fgInvert(color),
    listKey: overrideKey || entry.type + entry.id,
    pastEnd: isPastEnd(start, end),
  };
}

/**
 * Map internal planner task structure to gql PlannerTaskCreateInput|PlannerTaskEditInput
 */
export function mapPlannerTask(task: IEditEntry) {
  const mapped = {
    approvalState: task.approvalState.toUpperCase() as Approval_State,
    assignees: Object.keys(task.actorIds)
      .map(function (key) {
        return {
          id: (task.actorIds as any)[key][0],
          type: key.toUpperCase(),
        };
      })
      .filter((row) => row.id !== undefined),
    end: format(task.end, "yyyy-MM-dd'T'HH:mm:ssXXX"),
    start: format(task.start, "yyyy-MM-dd'T'HH:mm:ssXXX"),
    title: task.title,
    categoryId: task.categoryId,
    subtasks: (task?.subtasks || []).map(function (row) {
      const subtask = {
        id: row.subtaskId,
        title: row.title,
        sortOrder: row.sortorder,
      } as unknown as ISubTask;

      subtask.pictogramId = handlePictogram(row) ?? null;
      subtask.image = handleImage(row);

      return subtask;
    }),
    description: task.description,
    visibleForResident: task.visibleForResident,
    notificationSettings: Object.keys(task.notificationSettings)
      .filter(
        (key) =>
          Object.keys((task.notificationSettings as any)[key]).length !== 0,
      )
      .map(function (key) {
        return {
          notificationType: key.toUpperCase(),
          triggers: {
            change: Boolean(
              (task.notificationSettings as any)[key].triggers.change,
            ),
            complete: Boolean(
              (task.notificationSettings as any)[key].triggers.complete,
            ),
            create: Boolean(
              (task.notificationSettings as any)[key].triggers.create,
            ),
            overdue: Boolean(
              (task.notificationSettings as any)[key].triggers.overdue,
            ),
          },
          state: (task.notificationSettings as any)[key].state,
          notifications: {
            plannerFirst: (task.notificationSettings as any)[key].notifications
              .planner_first,
            plannerSecond: (task.notificationSettings as any)[key].notifications
              .planner_second,
            plannerThird: (task.notificationSettings as any)[key].notifications
              .planner_third,
            plannerFourth: (task.notificationSettings as any)[key].notifications
              .planner_fourth,
          },
        };
      }),
    isRepeat: task.isRepeat,
    pictogramId: undefined,
    image: undefined as any,
  };

  mapped.pictogramId = handlePictogram(task);
  mapped.image = handleImage(task);
  return mapped;
}

const toTaskAssigneeInput = (
  actorIds: IActorIds,
): PlannerTaskAssigneeInput[] => {
  return Object.keys(actorIds)
    .map(function (key) {
      return {
        id: (actorIds as any)[key][0],
        type: key.toUpperCase() as Plannertask_Assignee_Type,
      };
    })
    .filter((row) => row.id !== undefined);
};

const toPlannerNotificationSettingsInput = (
  assignees: PlannerTaskAssigneeInput[],
  notificationSettings: ITaskNotificationSettings,
): PlannerNotificationSettingsInput[] => {
  return Object.keys(notificationSettings || {})
    .filter(
      (key) => Object.keys((notificationSettings as any)[key]).length !== 0,
    )
    .map(function (key) {
      const profile: ProfileInput =
        key === "resident"
          ? notificationSettings.resident.assignee
          : notificationSettings.employee.assignee;
      const assignee = assignees.find(
        (item) =>
          item.type.toString() === profile.type.toString() &&
          item.id === profile.id,
      );

      if (!assignee) {
        return null;
      }

      const item: PlannerNotificationSettingsInput = {
        // notificationType: key.toUpperCase(),
        profile:
          key === "resident"
            ? notificationSettings.resident.assignee
            : notificationSettings.employee.assignee,
        triggerWhenAssignmentChange: Boolean(
          (notificationSettings as any)[key].triggers.change,
        ),
        triggerWhenAssignmentComplete: Boolean(
          (notificationSettings as any)[key].triggers.complete,
        ),
        triggerWhenAssignmentOverdue: Boolean(
          (notificationSettings as any)[key].triggers.overdue,
        ),
        triggerWhenNewAssignment: Boolean(
          (notificationSettings as any)[key].triggers.create,
        ),
        state: (notificationSettings as any)[key].state.toUpperCase(),
        reminderFirst: (notificationSettings as any)[key].notifications
          .planner_first,
        reminderSecond: (notificationSettings as any)[key].notifications
          .planner_second,
        reminderThird: (notificationSettings as any)[key].notifications
          .planner_third,
        reminderFourth: (notificationSettings as any)[key].notifications
          .planner_fourth,
      };

      return item;
    })
    .filter((item) => !!item);
};

const toPlannerSubtaskInput = (
  subtasks?: ISubTask[],
): PlannerSubtaskInput[] => {
  return (subtasks || []).map(function (row) {
    const subtask: PlannerSubtaskInput = {
      id: row.subtaskId,
      title: row.title,
      sortOrder: parseInt(row.sortorder),
    };

    subtask.pictogramId = handlePictogram(row) ?? null;
    subtask.image = handleImage(row);

    return subtask;
  });
};

export const toPlannerTaskCreateInput = (
  task: IEditEntry,
): PlannerTaskCreateInput => {
  const assignees = toTaskAssigneeInput(task.actorIds);
  return {
    approvalState: task.approvalState.toUpperCase() as Approval_State,
    assignees,
    categoryId: task.categoryId,
    description: task.description,
    end: format(task.end, "yyyy-MM-dd'T'HH:mm:ssXXX"),
    isRepeat: task.isRepeat,
    notificationSettings: toPlannerNotificationSettingsInput(
      assignees,
      task.notificationSettings,
    ).filter((item) => item.state !== Planner_Notification_State.None),
    image: handleImage(task),
    pictogramId: handlePictogram(task),
    rset: task.isRepeat
      ? {
          excludedDates: [],
          rule: toRRuleInput(task.rruleState),
        }
      : null,
    start: format(task.start, "yyyy-MM-dd'T'HH:mm:ssXXX"),
    subtasks: toPlannerSubtaskInput(task.subtasks),
    title: task.title || "",
    visibleForResident: task.visibleForResident,
  };
};

export const toPlannerTaskEditInput = (
  task: IEditEntry,
): PlannerTaskEditInput => {
  const assignees = toTaskAssigneeInput(task.actorIds);
  return {
    approvalState: task.approvalState.toUpperCase() as Approval_State,
    assignees: toTaskAssigneeInput(task.actorIds),
    categoryId: task.categoryId,
    description: task.description,
    end: format(task.end, "yyyy-MM-dd'T'HH:mm:ssXXX"),
    isRepeat: task.isRepeat,
    notificationSettings: toPlannerNotificationSettingsInput(
      assignees,
      task.notificationSettings,
    ),
    image: handleImage(task),
    pictogramId: handlePictogram(task),
    rset: task.isRepeat
      ? {
          excludedDates: [],
          rule: toRRuleInput(task.rruleState),
        }
      : null,
    start: format(task.start, "yyyy-MM-dd'T'HH:mm:ssXXX"),
    subtasks: toPlannerSubtaskInput(task.subtasks),
    title: task.title || "",
    visibleForResident: task.visibleForResident,
  };
};

export function handleImage(task: any): ImageUpload | undefined {
  // Second case is to support task creation from template with image
  if (
    (task.images.length > 0 && task.images[0]?.type == "image") ||
    (task.images[0]?.source == "upload" && "id" in task && !task.id)
  ) {
    return {
      body: task.images[0].image.startsWith("data:")
        ? (task.images[0].image as string)
        : null,
      url: task.images[0].image.startsWith("https:")
        ? task.images[0].image
        : null,
      container: Container_Type.Planner,
    };
  } else if (
    task.resourceId === null ||
    (task.images.length > 0 && task.images[0].type == "pictogram")
  ) {
    return undefined;
  }
}

export function handlePictogram(task: any) {
  if (task.images.length > 0 && task.images[0].type == "pictogram") {
    return task.images[0].id;
  } else if (task.images.length > 0 && task.images[0].type == "image") {
    return null;
  } else if (
    task.images.length > 0 &&
    task.images[0]?.source == "pictogram" &&
    task.images[0]?.saveAction == "delete"
  ) {
    return null;
  } else {
    return task.pictogramId;
  }
}

export function isPastEnd(start: Date, end: Date) {
  return (
    isBefore(end, startOfMinute(new Date())) && isSameDay(start, new Date())
  );
}

export function toLocalTimezone(date: any) {
  return new Date(format(new Date(date), "yyyy-MM-dd HH:mm:ss"));
}

export function isSameOrAfter(timeFirst: any, timeSecond: any) {
  return isSameMinute(timeFirst, timeSecond) || isAfter(timeFirst, timeSecond);
}

export function isSameOrBefore(timeFirst: any, timeSecond: any) {
  return isSameMinute(timeFirst, timeSecond) || isBefore(timeFirst, timeSecond);
}

export function isBetween(between: any, start: any, end: any) {
  return isAfter(between, start) && isBefore(between, end);
}

export const fillZero = (value: any) => {
  if (value.toString().length === 1) {
    return `0${value}`;
  }

  return value;
};

export const get12hTimes = () => {
  const period = (period: any) =>
    [12, ...Array.from(Array(11), (_: any, index: any) => index + 1)]
      .map((hour: any) => {
        return [0, 15, 30, 45].map((minute: any) => {
          return `${fillZero(hour)}:${fillZero(minute)}${period}`;
        });
      })
      .flat();

  return [period("am"), period("pm")].flat();
};

export const get24hTimes = () => {
  return Array.from(Array(24), (_, index) => index)
    .map((hour) => {
      return [0, 15, 30, 45].map((minute) => {
        return `${fillZero(hour)}:${fillZero(minute)}`;
      });
    })
    .flat();
};

export const toSelectItem = (value: string): ExtendedItem => {
  return {
    value,
    title: value,
  };
};

export const getTimes = (format: HourFormatTypes = "12h") => {
  if (format === "12h") {
    return get12hTimes().map(toSelectItem);
  }

  return get24hTimes().map(toSelectItem);
};

export const convertTo24Hour = (time12h: any) => {
  // Extracting hours, minutes, and am/pm from the time string
  const [time, period] = time12h.split(/(?=[ap]m)/i);
  // oxlint-disable-next-line prefer-const
  let [hours, minutes] = time.split(":");

  // Converting hours to integer
  hours = parseInt(hours, 10);

  // Adjusting hours based on am/pm
  if (period.toLowerCase() === "pm" && hours < 12) {
    hours += 12;
  } else if (period.toLowerCase() === "am" && hours === 12) {
    hours = 0;
  }

  // Formatting hours and minutes to 24-hour format
  const hours24 = hours < 10 ? "0" + hours : hours;
  const minutes24 = minutes;

  // Returning the time in 24-hour format
  return hours24 + ":" + minutes24;
};

export const convertTo12Hour = (time24h: any) => {
  // Extracting hours and minutes from the time string
  // oxlint-disable-next-line prefer-const
  let [hours, minutes] = time24h.split(":");

  // Converting hours to integer
  hours = parseInt(hours, 10);

  // Determining AM or PM
  const period = hours >= 12 ? "pm" : "am";

  // Adjusting hours to 12-hour format
  hours = hours % 12 || 12;

  // Formatting hours and minutes to 12-hour format
  const hours12 = hours < 10 ? "0" + hours : hours;
  const minutes12 = minutes;

  // Returning the time in 12-hour format
  return hours12 + ":" + minutes12 + period;
};
